import { useState, useCallback } from "react";

export const useRooms = (db, { showAlert, showConfirm }) => {
    const [rooms, setRooms] = useState([]);

    const retrieveRooms = useCallback(async () => {
        try {
            const rooms = await db.getTeacherRooms();
            if (!rooms) {
                setRooms([]);
                return;
            }
            if (Array.isArray(rooms)) {
                const formatedRooms = rooms.map((roomId) => ({
                    id: roomId.id,
                    name: roomId.name,
                    students: roomId.allowedStudents || [],
                    tags: roomId.tags || [],
                    isPrivate: roomId.isPrivate,
                    locked: roomId.locked,
                    notes: roomId.notes,
                }));
                setRooms(formatedRooms);
            } else setRooms([]);
        } catch (error) {
            showAlert(
                "Error",
                "No se pudieron cargar las salas: " + error.message,
            );
            setRooms([]);
        }
    }, [db, showAlert]);

    const handleToggleLock = useCallback(
        async (room) => {
            const newStatus = !room.isPrivate;
            try {
                await db.updateRoomPrivacy(room.id, newStatus);
                setRooms((prev) =>
                    prev.map((r) => {
                        if (r.id !== room.id) return r;
                        return { ...r, isPrivate: newStatus };
                    }),
                );
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo cambiar la privacidad de la sala ${room.name}: ${error.message}`,
                );
            }
        },
        [db, showAlert],
    );

    const handleCreateRoom = useCallback(
        async (name) => {
            try {
                if (rooms.some((r) => r.name === name)) {
                    showAlert(
                        "Error",
                        `Ya tienes una sala con el código ${name}`,
                    );
                    return;
                }

                if (await db.roomAlreadyExits(name)) {
                    showAlert(
                        "Error",
                        `La sala con el nombre ${name} ya existe. Prueba un nombre diferente`,
                    );
                    return;
                }

                const id = await db.addRoom(name);
                setRooms((prev) => [
                    ...prev,
                    { id, name, students: [], isPrivate: true },
                ]);
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo crear la sala ${name}: ${error.message}`,
                );
            }
        },
        [db, rooms, showAlert],
    );

    const handleDeleteRoom = useCallback(
        async (room) => {
            showConfirm({
                title: "Eliminar sala",
                message: `¿Seguro que quieres eliminar la sala "${room.name}" donde hay ${room.students.length} alumno${room.students.length == 0 || room.students.length > 1 ? "s" : ""}? Esta acción no se puede deshacer.`,
                confirm: "Eliminar",
                cancel: "Cancelar",
                danger: true,
                onConfirm: async () => {
                    try {
                        await db.deleteRoom(room.id);
                        setRooms((prev) =>
                            prev.filter((r) => r.id !== room.id),
                        );
                    } catch (error) {
                        showAlert(
                            "Error",
                            `No se pudo eliminar la sala ${room.name}: ${error.message}`,
                        );
                    }
                },
            });
        },
        [db, showAlert, showConfirm],
    );

    const handleAddStudent = useCallback(
        async (roomId, name) => {
            try {
                setRooms((prev) =>
                    prev.map((r) => {
                        if (r.id !== roomId) return r;
                        if (r.students.includes(name)) return r;

                        return { ...r, students: [...r.students, name] };
                    }),
                );

                await db.addStudent(roomId, name);
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo añadir al alumno ${name}: ${error.message}`,
                );
            }
        },
        [db, showAlert],
    );

    const handleRemoveStudent = useCallback(
        async (roomId, studentName) => {
            showConfirm({
                title: "Eliminar alumno",
                message: `¿Seguro que quieres eliminar a ${studentName} de la sala? Esta acción no se puede deshacer.`,
                confirm: "Eliminar",
                cancel: "Cancelar",
                danger: true,
                onConfirm: async () => {
                    try {
                        setRooms((prev) =>
                            prev.map((r) => {
                                if (r.id !== roomId) return r;
                                return {
                                    ...r,
                                    students: r.students.filter(
                                        (s) => s !== studentName,
                                    ),
                                };
                            }),
                        );
                        db.deleteStudent(roomId, studentName);
                    } catch (error) {
                        showAlert(
                            "Error",
                            `No se pudo eliminar al alumno ${studentName}: ${error.message}`,
                        );
                    }
                },
            });
        },
        [db, showAlert, showConfirm],
    );

    const handleAddTag = useCallback(
        async (id, tag) => {
            try {
                await db.apiRequest(`/api/rooms/${id}/tags`, {
                    method: "POST",
                    body: JSON.stringify({ tag }),
                });
                setRooms((prev) =>
                    prev.map((r) =>
                        r.id === id
                            ? { ...r, tags: [...(r.tags || []), tag] }
                            : r,
                    ),
                );
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo añadir la etiqueta: ${error.message}`,
                );
            }
        },
        [db, showAlert],
    );

    const handleRemoveTag = useCallback(
        async (id, tag) => {
            try {
                await db.apiRequest(
                    `/api/rooms/${id}/tags/${encodeURIComponent(tag)}`,
                    {
                        method: "DELETE",
                    },
                );
                setRooms((prev) =>
                    prev.map((r) =>
                        r.id === id
                            ? {
                                  ...r,
                                  tags: (r.tags || []).filter((t) => t !== tag),
                              }
                            : r,
                    ),
                );
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo eliminar la etiqueta: ${error.message}`,
                );
            }
        },
        [db, showAlert],
    );

    const handleMakeTagPrimary = useCallback(
        async (roomId, tag) => {
            const room = rooms.find((r) => r.id === roomId);
            if (!room) return;

            const currentTags = room.tags || [];
            if (currentTags[0] === tag) return;

            const newTags = [tag, ...currentTags.filter((t) => t !== tag)];

            try {
                setRooms((prev) =>
                    prev.map((r) =>
                        r.id === roomId ? { ...r, tags: newTags } : r,
                    ),
                );
                await db.apiRequest(`/api/rooms/${roomId}/tags`, {
                    method: "PUT",
                    body: JSON.stringify({ tags: newTags }),
                });
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo cambiar la etiqueta principal: ${error.message}`,
                );
                retrieveRooms();
            }
        },
        [db, rooms, showAlert, retrieveRooms],
    );

    const handleUpdateRoomNote = useCallback(
        async (roomId, note) => {
            const room = rooms.find((r) => r.id === roomId);
            if (!room) return;

            try {
                await db.apiRequest(`/api/rooms/${roomId}/notes`, {
                    method: "PUT",
                    body: JSON.stringify({ notes: note }),
                });
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo cambiar la nota de la sala: ${error.message}`,
                );
            }
        },
        [db, rooms, showAlert],
    );

    const handleLockRoom = useCallback(
        async (roomId) => {
            const room = rooms.find((r) => r.id === roomId);
            if (!room) return;

            const newStatus = !room.locked;

            try {
                await db.updateRoomLockState(room.id, newStatus);
                setRooms((prev) =>
                    prev.map((r) => {
                        if (r.id !== room.id) return r;
                        return { ...r, locked: newStatus };
                    }),
                );
            } catch (error) {
                showAlert(
                    "Error",
                    `No se pudo bloquear la sala: ${error.message}`,
                );
            }
        },
        [db, rooms, showAlert],
    );

    return {
        rooms,
        retrieveRooms,
        handleCreateRoom,
        handleDeleteRoom,
        handleToggleLock,
        handleAddStudent,
        handleRemoveStudent,
        handleAddTag,
        handleRemoveTag,
        handleMakeTagPrimary,
        handleUpdateRoomNote,
        handleLockRoom,
    };
};
